import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-md shadow-sm'
          : 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/50'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              Hipobuy
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                isHome ? 'text-primary-600' : 'text-foreground-800 hover:text-primary-500'
              }`}
            >
              {t('nav_home')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onMouseEnter={() => setDropdownOpen(true)}
                className={`text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                  dropdownOpen ? 'text-primary-600' : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 py-3 min-w-[260px] max-h-[420px] overflow-y-auto z-50"
                  onMouseLeave={() => setDropdownOpen(false)}
                  style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                >
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('nav_guides')}
            </a>

            <a
              href="#articles-seo"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('nav_articles')}
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('nav_contact')}
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cta_buy_now')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/50 pb-6 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-1 pt-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg flex items-center gap-2"
              >
                <i className="ri-home-4-line text-foreground-500"></i> {t('nav_home')}
              </Link>

              {/* Mobile Categories - Accordion */}
              <div className="px-4">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full py-3 text-sm font-semibold text-foreground-800 flex items-center justify-between cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-grid-line text-foreground-500"></i> {t('nav_categories')}
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileCatOpen && (
                  <div className="grid grid-cols-2 gap-1 pb-2 pl-7 border-l-2 border-primary-200 ml-1">
                    {categories.map((cat, idx) => (
                      <a
                        key={idx}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => { setMobileCatOpen(false); setMobileMenuOpen(false); }}
                        className="px-2 py-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg flex items-center gap-2"
              >
                <i className="ri-shopping-bag-3-line text-foreground-500"></i> {t('nav_guides')}
              </a>

              <a
                href="#articles-seo"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg flex items-center gap-2"
              >
                <i className="ri-article-line text-foreground-500"></i> {t('nav_articles')}
              </a>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg flex items-center gap-2"
              >
                <i className="ri-mail-line text-foreground-500"></i> {t('nav_contact')}
              </a>

              <div className="px-4 pt-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center px-5 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('cta_buy_now')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}